import React from 'react';
import { createRoot } from 'react-dom/client';

// Roots are cached per element so re-running an initializer (e.g. after a
// section re-render) reuses the existing root instead of warning/leaking.
const rootsByEl = new WeakMap();

/**
 * Render a component into an element. No-ops when the element is missing, so
 * callers don't need to guard for pages where the mount point doesn't exist.
 *
 * @param {Element|string|null} target element or CSS selector
 * @param {React.ComponentType} Component
 * @param {object} [props]
 * @param {{ strict?: boolean }} [options] `strict` wraps in StrictMode (dev-only effect)
 * @returns {boolean} whether a mount point was found
 */
export function renderInto(target, Component, props = {}, { strict = false } = {}) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) return false;

  let root = rootsByEl.get(el);
  if (!root) {
    root = createRoot(el);
    rootsByEl.set(el, root);
  }

  const element = React.createElement(Component, props);
  root.render(strict ? React.createElement(React.StrictMode, null, element) : element);
  return true;
}

/**
 * Run fn once the DOM is parsed. Webpack entries can execute before the mount
 * point exists when the script isn't deferred.
 * @param {() => void} fn
 */
export function onReady(fn) {
  if (typeof document === 'undefined') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}
